import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ClipboardList, Search, Filter, Download, User, Scale, Car, Shield } from "lucide-react";

export default function AuditTrail() {
  const [searchTerm, setSearchTerm] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("today");

  // Build query parameters for API call
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    
    if (eventTypeFilter !== "all") {
      params.append("eventType", eventTypeFilter);
    }
    
    // Handle date filtering
    if (dateFilter === "today") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      params.append("startDate", today.toISOString());
    } else if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      params.append("startDate", weekAgo.toISOString());
    } else if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      params.append("startDate", monthAgo.toISOString());
    }
    
    params.append("limit", "100");
    return params.toString();
  };

  // Fetch audit events from API using standard query fetcher
  const queryParams = buildQueryParams();
  const queryUrl = `/api/audit/events${queryParams ? `?${queryParams}` : ''}`;
  const { data: auditEvents = [], isLoading, error } = useQuery({
    queryKey: [queryUrl],
    // Uses default fetcher with proper authentication
  });

  // Add severity and userName fields that might be missing from database
  const processedEvents = auditEvents.map((event: any) => ({
    ...event,
    userName: event.userName || "System User",
    severity: event.severity || getSeverityFromEventType(event.eventType)
  }));

  function getSeverityFromEventType(eventType: string): string {
    switch (eventType) {
      case "emergency_stop":
      case "system_failure":
        return "critical";
      case "access_denied":
      case "validation_failed":
        return "warning";
      case "session_completed":
      case "visit_created":
        return "success";
      default:
        return "info";
    }
  }

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case "visit_created":
      case "access_denied":
        return <Car className="w-4 h-4" />;
      case "session_completed":
        return <Scale className="w-4 h-4" />;
      case "emergency_stop":
        return <Shield className="w-4 h-4" />;
      default:
        return <ClipboardList className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-orange-100 text-orange-800";
      case "success":
        return "bg-green-100 text-green-800";
      case "info":
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const formatEventDescription = (event: any) => {
    const details = event.details || {};
    switch (event.eventType) {
      case "visit_created":
        return `Vehicle ${details.plateNumber || 'Unknown'} entry granted for ${details.driverName || 'Unknown Driver'}`;
      case "session_completed":
        return `Weighing completed - Net: ${details.netWeight || 'N/A'}kg, Ticket: ${details.ticketNo || 'N/A'}`;
      case "access_denied":
        return `Access denied for vehicle ${details.plateNumber || 'Unknown'} - ${details.reason || 'No reason specified'}`;
      case "emergency_stop":
        return `Emergency stop activated - ${details.reason || 'Safety protocol'}`;
      case "scale_calibrated":
        return `Scale calibrated - Accuracy: ${details.accuracy || 'Standard'}`;
      default:
        return `${event.eventType.replace(/_/g, ' ')} event recorded`;
    }
  };

  const exportAuditLog = () => {
    const csvContent = [
      "Timestamp,Event Type,Entity,User,Description",
      ...filteredEvents.map(event => [
        new Date(event.timestamp).toLocaleString(),
        event.eventType,
        `${event.entityType}:${event.entityId}`,
        event.userName,
        formatEventDescription(event)
      ].map(field => `"${field}"`).join(","))
    ].join("\\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Client-side search filtering (API already handles event type and date filtering)
  const filteredEvents = processedEvents.filter(event => {
    if (searchTerm === "") return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      formatEventDescription(event).toLowerCase().includes(searchLower) ||
      event.userName.toLowerCase().includes(searchLower) ||
      event.entityId.toString().toLowerCase().includes(searchLower) ||
      event.eventType.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Audit Trail</h1>
              <p className="text-muted-foreground">System activity log and compliance tracking</p>
            </div>
            
            <Button
              onClick={exportAuditLog}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="button-export-audit"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Log
            </Button>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-events"
                  />
                </div>
                
                <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                  <SelectTrigger data-testid="select-event-type">
                    <SelectValue placeholder="Filter by event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="visit_created">Visit Created</SelectItem>
                    <SelectItem value="session_completed">Session Completed</SelectItem>
                    <SelectItem value="access_denied">Access Denied</SelectItem>
                    <SelectItem value="emergency_stop">Emergency Stop</SelectItem>
                    <SelectItem value="scale_calibrated">Scale Calibrated</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger data-testid="select-date-filter">
                    <SelectValue placeholder="Filter by date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Activity Log 
                {isLoading ? " (Loading...)" : ` (${filteredEvents.length} events)`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8" data-testid="loading-audit-events">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-2 text-muted-foreground">Loading audit events...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600" data-testid="error-audit-events">
                  <p>Failed to load audit events: {error.message}</p>
                  <Button 
                    className="mt-2" 
                    onClick={() => window.location.reload()}
                    data-testid="button-retry"
                  >
                    Retry
                  </Button>
                </div>
              ) : filteredEvents.length > 0 ? (
                <div className="space-y-4">
                  {filteredEvents.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-muted/30"
                      data-testid={`audit-event-${event.id}`}
                    >
                      <div className="flex-shrink-0 p-2 bg-muted rounded-lg">
                        {getEventIcon(event.eventType)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-foreground">
                              {formatEventDescription(event)}
                            </h4>
                            <Badge className={getSeverityColor(event.severity)}>
                              {event.severity}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="mt-1 flex items-center space-x-4 text-xs text-muted-foreground">
                          <div className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            {event.userName}
                          </div>
                          <div>
                            {event.entityType}:{event.entityId}
                          </div>
                        </div>
                        
                        {Object.keys(event.details).length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <details className="cursor-pointer">
                              <summary>View details</summary>
                              <pre className="mt-1 bg-muted p-2 rounded text-xs overflow-x-auto">
                                {JSON.stringify(event.details, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>No audit events found matching your criteria.</p>
                </div>
              )}
            </CardContent>
          </Card>
    </div>
  );
}